import clsx from "clsx";
import { useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import BackDrop from "../BackDrop";

import Card from "../Card";
import styles from "./BottomSheet.module.scss";
import { type CardProps } from "../Card/types";

export type BottomSheetProps = CardProps & {
  isOpen: boolean;
  open(): void;
  close(): void;
  onOpen?: (cb: (...args: any[]) => any) => void;
  onClose?: (cb: (...args: any[]) => any) => void;
  maxHeightMode?: "viewport" | "content"; // viewport: 80vh (default), content: max-content
};

export const useBottomSheet = () => {
  const [open, setOpen] = useState(false);
  const onCloseCB = useRef<(...args: any[]) => any>();
  const onOpenCB = useRef<(...args: any[]) => any>();
  return {
    isOpen: open,
    open() {
      const body = document.querySelector("body") as HTMLElement;
      body.style.overflow = "hidden";
      setOpen(true);
      onOpenCB.current?.call(onOpenCB.current);
    },
    close() {
      setOpen(false);
      const body = document.querySelector("body") as HTMLElement;
      body.style.overflow = "initial";
    },
    onOpen(cb: (...args: any[]) => any) {
      onOpenCB.current = cb;
    },
    onClose(cb: (...args: any[]) => any) {
      onCloseCB.current = cb;
    }
  };
};

const BottomSheet = (props: BottomSheetProps) => {
  const {
    isOpen,
    close,
    open: _open,
    maxHeightMode = "viewport",
    ...cardProps
  } = props;

  const bottomSheetClasses = clsx(
    cardProps.className,
    styles["bottom-sheet"],
    maxHeightMode === "content" && styles["bottom-sheet--max-content"]
  );

  return (
    <BackDrop open={isOpen} onClick={close}>
      <CSSTransition
        in={isOpen}
        unmountOnExit
        classNames={{
          enterDone: styles["bottom-sheet--enter-done"],
          exit: styles["bottom-sheet--exit"]
        }}
        timeout={{ enter: 0, exit: 300 }}
        appear
      >
        <Card
          {...cardProps}
          className={bottomSheetClasses}
          onClick={(e) => e.stopPropagation()}
        />
      </CSSTransition>
    </BackDrop>
  );
};

export default BottomSheet;
